// ════════════════════════════════════════════════════════════
// Tesorería — cartera de cobros y pagos, previsión de caja
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Alert, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  getVencimientos, getPrevision, liquidarVencimiento, crearVencimiento
} from '../api';

const eur = (n: number) =>
  (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const NOMBRE_TRAMO: any = {
  vencido: 'Vencido', '7_dias': 'Próximos 7 días', '30_dias': '8 a 30 días',
  '60_dias': '31 a 60 días', '90_dias': '61 a 90 días', mas_90: 'Más de 90 días',
};

export default function Tesoreria() {
  const [tab, setTab] = useState(0);
  const [datos, setDatos] = useState<any>(null);
  const [prevision, setPrevision] = useState<any[]>([]);
  const [filtros, setFiltros] = useState({ tipo: '', estado: 'pendiente', search: '', hasta: '' });
  const [liquidar, setLiquidar] = useState<any>(null);
  const [nuevo, setNuevo] = useState<any>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = () => {
    getVencimientos(filtros).then(setDatos).finally(() => setCargando(false));
    getPrevision().then((d: any) => setPrevision(d.tramos || [])).catch(() => {});
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtros]);

  const confirmarLiquidar = async () => {
    try {
      await liquidarVencimiento(liquidar.id, {
        importe: parseFloat(liquidar.importe_cobro),
        fecha: liquidar.fecha_cobro,
        notas: liquidar.notas_cobro,
      });
      setLiquidar(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al registrar');
    }
  };

  const guardarNuevo = async () => {
    try {
      await crearVencimiento(nuevo);
      setNuevo(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al crear');
    }
  };

  if (cargando) return <CircularProgress />;
  if (!datos || !datos.resumen) return (
    <Alert severity="error">
      No se pudieron cargar los datos de tesorería. Revisa el registro del servidor.
    </Alert>
  );

  const r = datos.resumen;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          🏦 Tesorería
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: '#4f46e5' }}
          onClick={() => setNuevo({ tipo: 'pago', concepto: '', tercero_nombre: '',
            fecha_vencimiento: new Date().toISOString().slice(0, 10), importe: '', notas: '' })}>
          Vencimiento manual
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #16a34a' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pendiente de cobro</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a' }}>{eur(r.total_cobros)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #dc2626' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pendiente de pago</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#dc2626' }}>{eur(r.total_pagos)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, bgcolor: r.cobros_vencidos > 0 ? '#fef2f2' : undefined }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Cobros vencidos</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800,
                color: r.cobros_vencidos > 0 ? '#dc2626' : 'inherit' }}>
                {eur(r.cobros_vencidos)}
              </Typography>
              {r.n_cobros_vencidos > 0 && (
                <Typography variant="caption" color="error">{r.n_cobros_vencidos} factura(s)</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Saldo previsto</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800,
                color: (r.total_cobros - r.total_pagos) >= 0 ? '#16a34a' : '#dc2626' }}>
                {eur(r.total_cobros - r.total_pagos)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="📋 Cartera" />
        <Tab label="📈 Previsión de caja" />
      </Tabs>

      {/* CARTERA */}
      {tab === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Buscar (cliente, nº documento)"
                value={filtros.search}
                onChange={(e) => setFiltros({ ...filtros, search: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={filtros.tipo} label="Tipo"
                  onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="cobro">Cobros</MenuItem>
                  <MenuItem value="pago">Pagos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select value={filtros.estado} label="Estado"
                  onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                  <MenuItem value="pendiente">Pendientes</MenuItem>
                  <MenuItem value="parcial">Parciales</MenuItem>
                  <MenuItem value="liquidado">Liquidados</MenuItem>
                  <MenuItem value="">Todos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="date" label="Hasta"
                InputLabelProps={{ shrink: true }} value={filtros.hasta}
                onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </Grid>
          </Grid>

          {datos.items.length === 0 ? (
            <Alert severity="info">No hay vencimientos con esos criterios.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Vence</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tercero</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Pendiente</TableCell>
                    <TableCell sx={{ width: 110 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datos.items.map((v: any) => (
                    <TableRow key={v.id} sx={{ bgcolor: v.vencido ? 'rgba(220,38,38,0.06)' : undefined }}>
                      <TableCell sx={{ fontWeight: v.vencido ? 700 : 400,
                        color: v.vencido ? '#dc2626' : 'inherit' }}>
                        {v.fecha_vencimiento}
                        {v.vencido && <Typography variant="caption" display="block">vencido</Typography>}
                      </TableCell>
                      <TableCell>{v.documento_numero || '—'}</TableCell>
                      <TableCell>{v.tercero_nombre || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={v.tipo === 'cobro' ? 'Cobro' : 'Pago'}
                          color={v.tipo === 'cobro' ? 'success' : 'error'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{eur(v.importe)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(v.pendiente)}</TableCell>
                      <TableCell align="right">
                        {v.estado !== 'liquidado' && (
                          <Button size="small" variant="outlined"
                            onClick={() => setLiquidar({ ...v, importe_cobro: v.pendiente,
                              fecha_cobro: new Date().toISOString().slice(0, 10), notas_cobro: '' })}>
                            {v.tipo === 'cobro' ? 'Cobrar' : 'Pagar'}
                          </Button>
                        )}
                        {v.estado === 'liquidado' && (
                          <Chip size="small" label="✓" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* PREVISIÓN */}
      {tab === 1 && (
        <Box>
<TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Tramo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Cobros</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Pagos</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Neto</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Acumulado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prevision.map((t: any) => (
                  <TableRow key={t.tramo}
                    sx={{ bgcolor: t.tramo === 'vencido' && (t.cobros > 0 || t.pagos > 0)
                      ? 'rgba(220,38,38,0.06)' : undefined }}>
                    <TableCell sx={{ fontWeight: 600 }}>{NOMBRE_TRAMO[t.tramo] || t.tramo}</TableCell>
                    <TableCell align="right" sx={{ color: '#16a34a' }}>{eur(t.cobros)}</TableCell>
                    <TableCell align="right" sx={{ color: '#dc2626' }}>{eur(t.pagos)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600,
                      color: t.neto >= 0 ? '#16a34a' : '#dc2626' }}>{eur(t.neto)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800,
                      color: t.acumulado >= 0 ? '#16a34a' : '#dc2626' }}>{eur(t.acumulado)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* DIALOG LIQUIDAR */}
      <Dialog open={!!liquidar} maxWidth="xs" fullWidth
        onClose={(e, rz) => { if (rz !== 'backdropClick' && rz !== 'escapeKeyDown') setLiquidar(null); }}>
        <DialogTitle>{liquidar?.tipo === 'cobro' ? '💰 Registrar cobro' : '💸 Registrar pago'}</DialogTitle>
        <DialogContent>
          {liquidar && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {liquidar.documento_numero} — {liquidar.tercero_nombre}<br />
                Pendiente: <b>{eur(liquidar.pendiente)}</b>
              </Typography>
              <TextField label="Importe" type="number" value={liquidar.importe_cobro}
                onChange={(e) => setLiquidar({ ...liquidar, importe_cobro: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                helperText="Puedes registrar un importe parcial." />
              <TextField label="Fecha" type="date" InputLabelProps={{ shrink: true }}
                value={liquidar.fecha_cobro}
                onChange={(e) => setLiquidar({ ...liquidar, fecha_cobro: e.target.value })} />
              <TextField label="Notas (opcional)" value={liquidar.notas_cobro}
                onChange={(e) => setLiquidar({ ...liquidar, notas_cobro: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiquidar(null)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarLiquidar} sx={{ bgcolor: '#4f46e5' }}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG VENCIMIENTO MANUAL */}
      <Dialog open={!!nuevo} maxWidth="xs" fullWidth
        onClose={(e, rz) => { if (rz !== 'backdropClick' && rz !== 'escapeKeyDown') setNuevo(null); }}>
        <DialogTitle>Nuevo vencimiento</DialogTitle>
        <DialogContent>
          {nuevo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select value={nuevo.tipo} label="Tipo"
                  onChange={(e) => setNuevo({ ...nuevo, tipo: e.target.value })}>
                  <MenuItem value="pago">Pago (salida)</MenuItem>
                  <MenuItem value="cobro">Cobro (entrada)</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Concepto" value={nuevo.concepto}
                placeholder="Ej: IVA 3T, nóminas, alquiler nave"
                onChange={(e) => setNuevo({ ...nuevo, concepto: e.target.value })} />
              <TextField label="Tercero (opcional)" value={nuevo.tercero_nombre}
                onChange={(e) => setNuevo({ ...nuevo, tercero_nombre: e.target.value })} />
              <TextField label="Fecha de vencimiento" type="date" InputLabelProps={{ shrink: true }}
                value={nuevo.fecha_vencimiento}
                onChange={(e) => setNuevo({ ...nuevo, fecha_vencimiento: e.target.value })} />
              <TextField label="Importe" type="number" value={nuevo.importe}
                onChange={(e) => setNuevo({ ...nuevo, importe: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevo(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarNuevo} sx={{ bgcolor: '#4f46e5' }}
            disabled={!nuevo?.importe || !nuevo?.fecha_vencimiento}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}